// 灰の戴冠獣の歩きを模型で（2026-11-03・作者の答え choice=model「正確な絵を描いてもらえるよう試行錯誤して作ってください」）。
//   今の配布版の歩きは絵2枚の切り替え（1枚 0.24 秒・体は等速で滑る・頭が 26px 跳ぶ・重心が動かない）
//   ＝docs/research/2026-11-03-hound-walk-lift-and-two-frame-walk.md。ここではまず動きを決める。
//   足の運び＝猫の仲間の常歩（なみあし）：後ろ左→前左→後ろ右→前右（同じ側の後ろ足が前足の 1/4 周期前に着く）。
//   いつも 2〜3本が床に着き、着いた足は床に止まる（床の目盛りと一緒に流れる＝滑っていない）。
//   体の上下＝肩も腰も、着いている脚が真下を通る時に一番高く、2本で支える時に一番低い（倒立振子）。
//   肩甲骨は前脚ごと振れる（脚が前なら肩も前）。頭は前足が着いた直後に一番下がる（1周に2回うなずく）。
//   単位＝実機の px（戴冠獣は主人公の 1.55 倍＝表示の高さ約 186px・き甲の高さ約 126px）。速さは実機の歩き 270px/秒。
//   11-04 作者「ちょこちょこ歩いてるように見える。４つ足で歩く動物ってこんなふうな骨格になってるの？」→ 骨格と歩幅を
//   実際の犬・獅子に合わせた版（HOUND_SKEL_REAL・HOUND_GAIT_REAL）を足した＝docs/research/2026-11-04-hound-walk-skeleton.md。
//   肩甲骨は斜め（上が後ろ）・肩の関節は胸の前・肘は胸の下・前腕は縦長。腰骨は斜め（上が前・股関節が後ろ下）・膝は前・踵は高く後ろ。

import { type MockupMove, type MockupPhase, type Pose, type Vec, easeInOut } from '../core'

export const HOUND_WALK_SPEED_PX_PER_S = 270

/** 骨組み（実機の px）。wit＝肩甲骨の上（き甲）・pel＝腰骨の上。角度は真下から測り、正で下の端が後ろへ行く */
export interface HoundSkeleton {
  witX: number; witY: number
  /** 肩甲骨の長さ・立った時の傾き・脚の前後に合わせて振れる大きさ（度） */
  scap: number; scapDeg: number; scapSwing: number
  upperArm: number; foreArm: number
  /** 手首から足先（手首が足先より後ろ・上） */
  carpus: Vec
  /** 前足が床に着いている間の真ん中（き甲からの前後） */
  foreFootX: number
  /** 前足の蹴り出しの終わりに手首が起きる角度（足先は床に残る・度） */
  wristRoll: number
  pelX: number; pelY: number
  /** 腰骨の長さ（0＝腰骨なし・pel が股関節）・傾き・振れ（度） */
  pel: number; pelDeg: number; pelSwing: number
  thigh: number; shin: number
  /** 足先から踵（踵が後ろ・上） */
  meta: Vec
  /** 後ろ足が床に着いている間の真ん中（pel からの前後） */
  hindFootX: number
  /** 後ろ足の蹴り出しの終わりに踵が上がる角度（足先は床に残る・度） */
  heelLift: number
  /** 振り出しの前半に後ろの足先を後ろへ畳む角度（踵の上がりに足す・度） */
  hindFold: number
  /** 頭と首（き甲から）・尾（pel から） */
  head: Vec; neck: Vec; tail: [Vec, Vec, Vec]
}

/** 最初の模型（11-03）：肩甲骨が縦・肩の関節と股関節が高くて近い（胴が短い） */
export const HOUND_SKEL_FIRST: HoundSkeleton = {
  witX: 46, witY: -126, scap: 34, scapDeg: 0, scapSwing: 16, upperArm: 40, foreArm: 52, carpus: [4, 18], foreFootX: 2, wristRoll: 0,
  pelX: -52, pelY: -96, pel: 0, pelDeg: 0, pelSwing: 0, thigh: 46, shin: 48, meta: [-8, -32], hindFootX: 4, heelLift: 25, hindFold: 50,
  head: [74, 22], neck: [22, 6], tail: [[-22, -18], [-70, -4], [-92, 52]],
}
/** 骨格を直した版（11-04）：犬・獅子の骨の並び。肩の関節と股関節が低く、前後に離れる（胴が長い） */
export const HOUND_SKEL_REAL: HoundSkeleton = {
  witX: 40, witY: -124, scap: 50, scapDeg: -32, scapSwing: 18, upperArm: 34, foreArm: 48, carpus: [6, 16], foreFootX: 5, wristRoll: 70,
  pelX: -44, pelY: -120, pel: 44, pelDeg: 30, pelSwing: 6, thigh: 36, shin: 40, meta: [-7, -30], hindFootX: -31, heelLift: 55, hindFold: 15,
  head: [80, 34], neck: [26, 10], tail: [[-16, 6], [-62, 20], [-84, 76]],
}

export interface HoundGait {
  /** 1周で進む長さ（px）＝同じ足が次に着くまで */
  stridePx: number
  /** 1本の足が床に着いている割合 */
  duty: number
  /** 肩・腰の上下の大きさ（px） */
  bobFore: number
  bobHind: number
  /** 振り出す足の上がる高さ（手首・踵・px） */
  liftFore: number
  liftHind: number
  skel: HoundSkeleton
  /** 体の進む速さ（px/秒・省略＝実機の 270） */
  speed?: number
}

const spd = (g: HoundGait): number => g.speed ?? HOUND_WALK_SPEED_PX_PER_S

/** 4本の足：着く時刻（周期の中の位置）。後ろ左→前左→後ろ右→前右。N＝手前（左）・F＝奥（右） */
export const HOUND_FEET = [
  { id: 'HN', label: '手前の後ろ足', fore: false, phase: 0 },
  { id: 'FN', label: '手前の前足', fore: true, phase: 0.25 },
  { id: 'HF', label: '奥の後ろ足', fore: false, phase: 0.5 },
  { id: 'FF', label: '奥の前足', fore: true, phase: 0.75 },
] as const

const rot = (v: Vec, deg: number): Vec => {
  const a = deg * Math.PI / 180
  return [v[0] * Math.cos(a) - v[1] * Math.sin(a), v[0] * Math.sin(a) + v[1] * Math.cos(a)]
}
const add = (a: Vec, b: Vec): Vec => [a[0] + b[0], a[1] + b[1]]
const mod1 = (x: number): number => ((x % 1) + 1) % 1

/** 2本の骨で a から b へ届く真ん中の関節。bend=+1 は後ろへ曲がる（肘）・-1 は前へ（膝） */
export function ik2(a: Vec, b: Vec, l1: number, l2: number, bend: 1 | -1): { mid: Vec; reach: number } {
  const dx = b[0] - a[0], dy = b[1] - a[1], dist = Math.hypot(dx, dy)
  const d = Math.min(l1 + l2 - 0.01, Math.max(Math.abs(l1 - l2) + 0.01, dist))
  const cosA = (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d)
  const alpha = Math.acos(Math.max(-1, Math.min(1, cosA))) * bend
  const ux = dx / dist, uy = dy / dist
  const mid: Vec = [a[0] + l1 * (ux * Math.cos(alpha) - uy * Math.sin(alpha)), a[1] + l1 * (ux * Math.sin(alpha) + uy * Math.cos(alpha))]
  return { mid, reach: dist - d }
}

/** 足1本の、体（肩・腰の真下）から見た前後の位置と、振り出しの進み（床に着いている間は null） */
function footState(g: HoundGait, u: number, phase: number): { dx: number; swing: number | null } {
  const d = g.duty * g.stridePx / 2, ul = mod1(u - phase)
  if (ul < g.duty) return { dx: d - (ul / g.duty) * 2 * d, swing: null }
  const s = (ul - g.duty) / (1 - g.duty)
  return { dx: -d + 2 * d * easeInOut(s), swing: s }
}

/** 肩（または腰）の高さのずれ：着いている脚のうち一番真下に近い脚で決まる（真下で一番高い） */
function girdleBob(g: HoundGait, u: number, fore: boolean, amp: number): number {
  const d = g.duty * g.stridePx / 2
  let m = d
  for (const f of HOUND_FEET) {
    if (f.fore !== fore) continue
    const st = footState(g, u, f.phase)
    if (st.swing === null) m = Math.min(m, Math.abs(st.dx))
  }
  return -amp * (1 - (m / d) ** 2) + amp / 2
}

export function houndWalkPose(g: HoundGait, tMs: number): Pose & { reachMiss: number } {
  const T = g.stridePx / spd(g) * 1000
  const u = mod1(tMs / T)
  const d = g.duty * g.stridePx / 2
  const K = g.skel
  const yF = K.witY + girdleBob(g, u, true, g.bobFore), yH = K.pelY + girdleBob(g, u, false, g.bobHind)
  const wit: Vec = [K.witX, yF], pel: Vec = [K.pelX, yH]
  // 腰骨は左右の後ろ脚の前後の平均に合わせてわずかに振れる（股関節は1つの点で代表する）
  let hindMean = 0
  for (const f of HOUND_FEET) if (!f.fore) hindMean += footState(g, u, f.phase).dx / d / 2
  const hip: Vec = K.pel > 0 ? add(pel, rot([0, K.pel], K.pelDeg - K.pelSwing * hindMean)) : pel
  const o: Record<string, Vec | number> = { wit, hip }
  if (K.pel > 0) o.pel = pel
  let reachMiss = 0
  for (const f of HOUND_FEET) {
    const st = footState(g, u, f.phase)
    if (f.fore) {
      // 肩甲骨は脚の前後に合わせて振れる（脚が前なら肩の関節も前）
      const scapDeg = K.scapDeg - K.scapSwing * (st.dx / d)
      const j = add(wit, rot([0, K.scap], scapDeg))
      const paw0: Vec = [K.witX + K.foreFootX + st.dx, 0]
      let wrist: Vec, paw: Vec
      if (st.swing === null) {
        // 蹴り出しの後半に手首が起きる（足先は床に残る）
        paw = paw0; wrist = add(paw, rot([-K.carpus[0], -K.carpus[1]], K.wristRoll * Math.max(0, -st.dx / d)))
      } else {
        // 振り出し：手首が弧を描き、足先は前半に後ろへ折りたたまれ、着く前に伸びる
        const s = st.swing, roll = K.wristRoll * (1 - s)
        wrist = add(add(paw0, rot([-K.carpus[0], -K.carpus[1]], roll)), [0, -g.liftFore * Math.sin(Math.PI * s)])
        const flex = roll + 100 * Math.sin(Math.PI * Math.min(1, s / 0.75))
        paw = add(wrist, rot(K.carpus, flex))
        paw[1] = Math.min(0, paw[1])
      }
      const k = ik2(j, wrist, K.upperArm, K.foreArm, 1)
      reachMiss = Math.max(reachMiss, k.reach)
      o[`jF${f.id[1]}`] = j; o[`eF${f.id[1]}`] = k.mid; o[`wF${f.id[1]}`] = wrist; o[`pF${f.id[1]}`] = paw
    } else {
      const paw0: Vec = [K.pelX + K.hindFootX + st.dx, 0]
      let hock: Vec, paw: Vec
      if (st.swing === null) {
        // 蹴り出しの後半に踵が上がる（足先は床に残る）
        const heel = K.heelLift * Math.max(0, -st.dx / d)
        paw = paw0; hock = add(paw, rot(K.meta, heel))
      } else {
        // 振り出し：踵の上がりを蹴り出しの終わりから引き継いで戻しつつ、足先を後ろへ畳んで持ち上げる
        const s = st.swing, heel = K.heelLift * (1 - s)
        hock = add(add(paw0, rot(K.meta, heel)), [0, -g.liftHind * Math.sin(Math.PI * s)])
        const flex = heel + K.hindFold * Math.sin(Math.PI * Math.min(1, s / 0.7))
        paw = add(hock, rot([-K.meta[0], -K.meta[1]], flex))
        paw[1] = Math.min(0, paw[1])
      }
      const k = ik2(hip, hock, K.thigh, K.shin, -1)
      reachMiss = Math.max(reachMiss, k.reach)
      o[`kH${f.id[1]}`] = k.mid; o[`hH${f.id[1]}`] = hock; o[`pH${f.id[1]}`] = paw
    }
  }
  // 頭は前足が着いた直後に一番下がる（1周に2回）・少し遅れて肩についていく
  const nodY = 4 * Math.cos(4 * Math.PI * (u - 0.32))
  o.neck = add(wit, K.neck)
  o.head = add(wit, [K.head[0], K.head[1] + nodY])
  o.nod = -nodY * 1.2
  const sway = 6 * Math.sin(2 * Math.PI * (u - 0.2))
  o.tail0 = add(pel, K.tail[0]); o.tail1 = add(pel, K.tail[1]); o.tail2 = add(pel, [K.tail[2][0] + sway * 0.4, K.tail[2][1] + sway])
  return { ...(o as Pose), rx: 0, ry: 0, reachMiss }
}

/** t（ms）に床に着いている足（HN・FN・HF・FF） */
export function houndStanceFeet(g: HoundGait, tMs: number): string[] {
  const u = mod1(tMs / (g.stridePx / spd(g) * 1000))
  return HOUND_FEET.filter(f => footState(g, u, f.phase).swing === null).map(f => f.id)
}

/** 4本の足が着く時刻（ms）。拍の帯に出す */
export function houndFootfalls(g: HoundGait, cycles: number): { at: number; ms: number; foot: string }[] {
  const T = g.stridePx / spd(g) * 1000, out: { at: number; ms: number; foot: string }[] = []
  for (let c = 0; c < cycles; c++) for (const f of HOUND_FEET) out.push({ at: (c + f.phase) * T, ms: 40, foot: f.id })
  return out
}

function walkMove(id: string, label: string, g: HoundGait, cycles: number, note: string): MockupMove {
  const T = g.stridePx / spd(g) * 1000, dur = Math.round(T * cycles * 1000) / 1000
  const phases: MockupPhase[] = [{ from: 0, to: dur, label, kind: 'rest' }]
  return {
    id, boss: '灰の戴冠獣', label, body: 'quad', durMs: dur, scale: 0.9, baseX: 205, heroAt: -2000,
    keys: [], poseFn: t => houndWalkPose(g, t), floorScrollPerMs: spd(g) / 1000,
    phases, hits: [], activeMs: 0, box: [0, 0, 0, 0], paths: ['pFN', 'pHN'],
    cues: houndFootfalls(g, cycles).map(({ at, ms }) => ({ at, ms })),
    source: note,
  }
}

/** 案A 大股：1周 160px（約 0.59 秒）。1歩が重く、肩と腰の揺れが大きい */
export const HOUND_GAIT_LONG: HoundGait = { stridePx: 160, duty: 0.6, bobFore: 6, bobHind: 4, liftFore: 20, liftHind: 14, skel: HOUND_SKEL_FIRST }
/** 案B 小股：1周 130px（約 0.48 秒）。今の配布版と同じ歩幅で、足をせかせか運ぶ */
export const HOUND_GAIT_SHORT: HoundGait = { stridePx: 130, duty: 0.6, bobFore: 4, bobHind: 3, liftFore: 15, liftHind: 11, skel: HOUND_SKEL_FIRST }
/** 骨格を直した版：1周 200px（約 0.74 秒）。歩幅はき甲の高さの約 1.6 倍（犬の常歩の範囲）。脚の長さで届く一番長い歩幅 */
export const HOUND_GAIT_REAL: HoundGait = { stridePx: 200, duty: 0.6, bobFore: 4, bobHind: 3, liftFore: 18, liftHind: 13, skel: HOUND_SKEL_REAL }

export const HOUND_WALK_LONG_MOCKUP = walkMove('hound-walk-long', '歩き・案A 広い歩幅（1周 0.59秒）', HOUND_GAIT_LONG, 4,
  '速さは実機の歩き（270px/秒）。足の運び＝後ろ左→前左→後ろ右→前右。床の刻みは体の速さで流れる＝着いた足が刻みと一緒に流れていれば滑っていない。')
export const HOUND_WALK_SHORT_MOCKUP = walkMove('hound-walk-short', '歩き・案B 狭い歩幅（1周 0.48秒）', HOUND_GAIT_SHORT, 5,
  '速さは実機の歩き（270px/秒）。歩幅は今の配布版（1周 130px）と同じ。')
export const HOUND_WALK_REAL_MOCKUP = walkMove('hound-walk-real', '歩き・体の組み方を直した版（1周 0.74秒）', HOUND_GAIT_REAL, 3,
  '速さは実機の歩き（270px/秒）。肩と腰を斜めに組み、肘を胸の下・膝を腹の下へ下げ、前の脚を縦の柱にし、歩幅を肩の高さの約1.6倍にした。足の運びは同じ（後ろ左→前左→後ろ右→前右）。')
/** 骨格を直した版で、歩く速さを 3/4（200px/秒）にした比べ用。歩幅は同じで1周 1.0 秒（配布版の速さは変えていない） */
export const HOUND_GAIT_REAL_SLOW: HoundGait = { ...HOUND_GAIT_REAL, speed: 200 }
export const HOUND_WALK_REAL_SLOW_MOCKUP = walkMove('hound-walk-real-slow', '歩き・体の組み方を直した版・歩く速さ 3/4（1周 1.0秒）', HOUND_GAIT_REAL_SLOW, 3,
  '体の組み方と歩幅は「体の組み方を直した版」と同じ。体の進む速さだけ 270→200px/秒（比べ用・配布版は 270 のまま）。')
